import { createContext, useContext, type ReactNode } from 'react';
import { View, type ViewProps } from 'react-native';

const FormSubmitContext = createContext<(() => void) | null>(null);

/** Submit handler of the enclosing <Form>, or null when not inside one. */
export function useFormSubmit() {
  return useContext(FormSubmitContext);
}

export interface FormProps extends ViewProps {
  /** Fired when a single-line field inside the form is submitted (Enter / return). */
  onSubmit: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * Groups form fields and exposes its `onSubmit` to descendant fields via context,
 * so single-line `TextField`/`PasswordField` inputs submit on Enter (web) / return
 * (native) without per-field wiring. On web `role="form"` renders a real
 * `<form>` element; the per-field `onSubmitEditing` wiring is what keeps that element
 * from triggering a native page-reload submit (RNW only preventDefaults Enter when a
 * field has an `onSubmitEditing` handler).
 */
export function Form({ onSubmit, className, children, ...props }: FormProps) {
  return (
    <FormSubmitContext.Provider value={onSubmit}>
      {/* Renders a real <form> on web (semantic/accessible); a11y trait on native. */}
      <View role="form" className={className} {...props}>
        {children}
      </View>
    </FormSubmitContext.Provider>
  );
}
